import {
  Box,
  Button,
  InputGroup,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'brokertslib/src/components'
import { FormProvider, useFieldArray, useForm } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Datepicker } from '../../components/Datepicker'
import type { Rtpc } from '../../dtos/rtpc/Rtpc'
import { useGetInsuranceCategoryInsurers } from '../../hooks/useRtpc'
import { Add, Save, Settings, Trash } from '../../icons'
import { rtpcFilterContext } from './RtpcFilterContext'

export interface RtpcListProps {
  items: Rtpc[]
  isLoading: boolean
  onSubmit: (e: { items: Rtpc[] }) => Promise<void>
  onDeleteItem: (rtpc: Rtpc) => Promise<void>
}

export const RtpcListForm = ({
  items,
  isLoading,
  onSubmit,
  onDeleteItem,
}: RtpcListProps) => {
  const filterContext = useContext(rtpcFilterContext)
  const methods = useForm<{ items: Rtpc[] }>({
    values: { items: [...items] },
  })
  const {
    data: insuranceCategoryInsurerData,
    isLoading: isIciLoading,
    getInsuranceCategoryInsurerText,
  } = useGetInsuranceCategoryInsurers()
  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    control,
    watch,
  } = methods
  const navigate = useNavigate()

  useEffect(() => {
    reset({ items: [...items] })
  }, [items, reset])

  const { fields } = useFieldArray({
    name: 'items',
    control,
  })

  const addNewRow = () => {
    setValue('items', [
      ...fields,
      { valid: true, coef: 1, deleted: false } as Rtpc,
    ])
  }

  const removeRtpc = (rtpc: Rtpc & { id: string }) => {
    if (!rtpc.rtpcId)
      setValue(
        'items',
        fields.filter((x) => x.id !== rtpc.id),
      )
    else
      onDeleteItem(rtpc).then(() =>
        setValue(
          'items',
          fields.filter((x) => x.id !== rtpc.id),
        ),
      )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
          <Table>
            <Thead>
              <Tr>
                <Th w={0}>ID</Th>
                <Th w={'80'}>Pojišťovna</Th>
                <Th w={0}>Koeficient</Th>
                <Th>Platnost od</Th>
                <Th>Platnost do</Th>
                <Th>Platnost</Th>
                <Th w={0}></Th>
                <Th w={0}></Th>
              </Tr>
            </Thead>
            <Tbody>
              <TableSkeleton
                rowsCount={5}
                colSpan={7}
                isLoaded={!isLoading && !isIciLoading}
              >
                <EmptyTablePlaceholder
                  text={'Žádné organizace k zobrazení'}
                  colSpan={7}
                >
                  {fields.map((item, index) => {
                    return (
                      <Tr key={index}>
                        <Th>{item.rtpcId}</Th>
                        <Td>
                          <Select
                            isInvalid={
                              !!errors.items?.at!(index)
                                ?.insuranceCategoryInsurerId
                            }
                            width={'80'}
                            {...register(
                              `items.${index}.insuranceCategoryInsurerId`,
                              { required: true },
                            )}
                          >
                            {!item.rtpcId && (
                              <option value="">&gt; Vyberte pojišťovnu</option>
                            )}
                            {insuranceCategoryInsurerData?.data
                              .filter(
                                (ici) =>
                                  (!filterContext.product ||
                                    ici.insuranceCategoryTypeId ===
                                    +filterContext.product) &&
                                  (!filterContext.insurer ||
                                    ici.insurerId === +filterContext.insurer),
                              )
                              .map((ici, index) => (
                                <option key={index} value={ici.id}>
                                  {getInsuranceCategoryInsurerText(ici.id)}
                                </option>
                              ))}
                          </Select>
                        </Td>
                        <Td>
                          <InputGroup>
                            <NumberInput w={'24'} step={0.01}>
                              <NumberInputField
                                {...register(`items.${index}.coef`, {
                                  valueAsNumber: true,
                                })}
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </InputGroup>
                        </Td>
                        <Td w={0}>
                          <Datepicker
                            showIcon={false}
                            htmlSize={10}
                            options={{ enableTime: true }}
                            date={getValues(`items.${index}.validFrom`)}
                            onDateChange={(date) =>
                              setValue(`items.${index}.validFrom`, date)
                            }
                          />
                        </Td>
                        <Td w={0}>
                          <Datepicker
                            showIcon={false}
                            htmlSize={10}
                            options={{ enableTime: true }}
                            date={getValues(`items.${index}.validTo`)}
                            onDateChange={(date) =>
                              setValue(`items.${index}.validTo`, date)
                            }
                          />
                        </Td>
                        <Td>
                          <Switch
                            id="valid"
                            size="sm"
                            isChecked={watch(`items.${index}.valid`)}
                            onChange={(e) =>
                              setValue(`items.${index}.valid`, e.target.checked)
                            }
                          />
                        </Td>
                        {item.rtpcId && !item.deleted && (
                          <Td p={0} pr={1}>
                            <Button
                              colorScheme="primary"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                navigate(`${item.rtpcId}/setOrganizations`)
                              }
                              title="Nastavit organizace"
                            >
                              <Settings />
                            </Button>
                          </Td>
                        )}
                        <Td p={0} pr={1} colSpan={item.deleted ? 2 : 1}>
                          {item.deleted ? (
                            <Text
                              textAlign={'right'}
                              mr={2}
                              fontSize={'xs'}
                              fontWeight={'light'}
                            >
                              Smazáno
                            </Text>
                          ) : (
                            <Button
                              type="button"
                              colorScheme="secondary"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeRtpc(item)}
                              title="Smazat"
                            >
                              <Trash boxSize={4} />
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                  <Tr>
                    <Td p={0} colSpan={8}>
                      <Button
                        _focus={{ boxShadow: 'none' }}
                        w={'100%'}
                        colorScheme="primary"
                        size="md"
                        variant="ghost"
                        justifyContent={'start'}
                        onClick={addNewRow}
                      >
                        <Add /> Nový záznam
                      </Button>
                    </Td>
                  </Tr>
                </EmptyTablePlaceholder>
              </TableSkeleton>
            </Tbody>
          </Table>
          <Button
            type="submit"
            colorScheme="primary"
            width={'36'}
            isLoading={isSubmitting}
          >
            <Save /> Uložit
          </Button>
        </Box>
      </form>
    </FormProvider>
  )
}
